import React, {
  type ButtonHTMLAttributes,
  type FC,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from 'react'
import { cn } from 'shared-utilities'

export type Button2026Variant = 'primary' | 'secondary' | 'outlined' | 'text'

export interface Button2026LinkProps extends HTMLAttributes<HTMLAnchorElement> {
  href: string
  addClass?: string
  children?: ReactNode
  onClick?: MouseEventHandler<HTMLElement>
}

export interface Button2026Props
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: Button2026Variant
  href?: string
  icon?: ReactNode
  className?: string
  onClick?: MouseEventHandler<HTMLElement>
  LinkComponent?: FC<Button2026LinkProps>
  children: ReactNode
}

const baseClasses =
  'inline-flex items-center max-w-full justify-center whitespace-nowrap transition-[box-shadow,background,gap] duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-60'

const variantClasses: Record<Button2026Variant, string> = {
  primary: cn(
    'h-12 gap-2.5 rounded-[40px] px-[79px] py-3.5 font-sora text-base leading-normal text-2026-primary',
    'bg-2026-cta shadow-2026-cta',
    'hover:bg-2026-cta-hover focus-visible:bg-2026-cta-hover',
    'hover:shadow-2026-cta-hover focus-visible:shadow-2026-cta-hover',
    'disabled:hover:bg-2026-cta disabled:hover:shadow-2026-cta'
  ),
  secondary: cn(
    'h-10 gap-3 rounded px-5 font-inter text-sm font-semibold tracking-[0.5px] text-white',
    'bg-2026-green-tertiary'
  ),
  outlined: cn(
    'h-12 gap-2.5 rounded-[40px] border border-2026-yellow bg-transparent px-6 py-3.5 font-sora text-base leading-normal text-2026-yellow',
    'hover:bg-2026-yellow/10 focus-visible:bg-2026-yellow/10'
  ),
  text: cn(
    'h-12 gap-2.5 bg-transparent px-0 py-3.5 font-sora text-base leading-normal text-2026-yellow',
    'hover:gap-3.5 focus-visible:gap-3.5'
  ),
}

const Button2026 = ({
  variant = 'primary',
  href,
  icon,
  children,
  className,
  disabled,
  type = 'button',
  onClick,
  LinkComponent,
  ...props
}: Button2026Props) => {
  const classes = cn(baseClasses, variantClasses[variant], className)

  const content = (
    <>
      <span>{children}</span>
      {icon}
    </>
  )

  if (href && !disabled) {
    if (LinkComponent) {
      return (
        <LinkComponent href={href} addClass={classes} onClick={onClick}>
          {content}
        </LinkComponent>
      )
    }

    return (
      <a href={href} className={classes} onClick={onClick}>
        {content}
      </a>
    )
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  )
}

export default Button2026
